/**
 * Custom (user-authored) codes saved to Firestore at /codebook/{id}.
 *
 * Anyone signed in can read all codes. Only the author can update/delete
 * their own. IDs use a "C-XXXXXX" prefix so they never collide with the
 * built-in codes ("01"–"32" from codebook.ts).
 */

import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  setDoc,
  type Unsubscribe,
} from "firebase/firestore";
import { getFirebase } from "../firebase";
import type { CodeSentence, FrameColor } from "../game/types";

export interface CustomCode extends CodeSentence {
  authorUid: string;
  authorName: string;
  createdAt: number;
  updatedAt: number;
}

export function generateCodeId(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let id = "C-";
  for (let i = 0; i < 6; i++) id += chars[Math.floor(Math.random() * chars.length)];
  return id;
}

/** Default frame colour for an icon picked from board N — the theme of each
 * board strongly suggests its grammatical role, so we pre-populate this and
 * the user can override per token. */
export function defaultColorForBoard(boardId: number): FrameColor {
  switch (boardId) {
    case 1: return "noun";    // Persons
    case 2: return "verb";    // Actions
    case 3: return "verb";    // Objects + Actions (mostly verbs)
    case 4: return "noun";    // Objects
    case 5: return "adverb";  // Times
    case 6: return "noun";    // Places
    case 7: return "adj";     // Adjectives
    case 8: return "prep";    // Conj + Prep + Adv
    default: return "noun";
  }
}

export async function saveCustomCode(code: CustomCode): Promise<void> {
  const { db } = getFirebase();
  await setDoc(doc(db, "codebook", code.id), code);
}

export async function deleteCustomCode(id: string): Promise<void> {
  const { db } = getFirebase();
  await deleteDoc(doc(db, "codebook", id));
}

export async function loadCustomCodes(): Promise<CustomCode[]> {
  const { db } = getFirebase();
  const snap = await getDocs(collection(db, "codebook"));
  return snap.docs
    .map((d) => d.data() as CustomCode)
    .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
}

export function watchCustomCodes(cb: (codes: CustomCode[]) => void): Unsubscribe {
  const { db } = getFirebase();
  return onSnapshot(collection(db, "codebook"), (snap) => {
    cb(
      snap.docs
        .map((d) => d.data() as CustomCode)
        .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0)),
    );
  });
}
